import React from 'react';
import { useNavigate } from 'react-router-dom';

// Icons for recovery metrics
const HRVIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
  </svg>
);

const StressIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
    <line x1="9" y1="9" x2="9.01" y2="9"></line>
    <line x1="15" y1="9" x2="15.01" y2="9"></line>
  </svg>
);

  const Recovery = () => {
  const navigate = useNavigate();



  const handleCardClick = (metricType) => {
    // Handle card click - could open detailed view or measurement dialog
    console.log(`Clicked on ${metricType} metric`);
  };

  const recoveryMetrics = [
    {
      id: 'hrv',
      name: 'Heart Rate Variability',
      bengaliName: 'Heart Rate Variability',
      icon: <HRVIcon />,
      description: 'Track your HRV for stress and recovery insights',
      color: 'bg-[#58A6FF]',
      unit: 'ms',
      currentValue: '45',
      canMeasure: true
    },
    {
      id: 'stress',
      name: 'Stress Level',
      bengaliName: 'Stress Level',
      icon: <StressIcon />,
      description: 'Monitor your stress levels and recovery status',
      color: 'bg-[#58A6FF]',
      unit: '',
      currentValue: '25',
      canMeasure: true
    }
  ];

  return (
    <div className="min-h-screen pt-14 pb-20">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium text-white">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Recovery & Stress</h1>
          <p className="text-white">Monitor your recovery metrics and stress levels</p>
        </div>

        {/* Recovery Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {recoveryMetrics.map((metric, index) => (
            <div
              key={metric.id}
              onClick={() => handleCardClick(metric.id)}
              className={`bg-[#161B22] rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group border border-[#30363D] ${
                index === recoveryMetrics.length - 1 ? 'mb-20' : ''
              }`}
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">{metric.bengaliName}</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-white text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>
                
                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-white">{metric.currentValue}</span>
                    <span className="text-white ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">Current Value</div>
                    <div className="text-xs text-green-600">+2.3%</div>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white">Average</span>
                      <div className="font-medium text-white">{metric.currentValue} {metric.unit}</div>
                    </div>
                    <div>
                      <span className="text-white">Maximum</span>
                      <div className="font-medium text-white">{Math.round(parseInt(metric.currentValue) * 1.2)} {metric.unit}</div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/landing/tab3/VisualizeWellnessData", { 
                        state: { dataType: metric.id } 
                      });
                    }}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    View Data
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>


        
      </div>
    </div>
  );
};

export default Recovery; 